/**
 * Social Engineering Icons Registry
 *
 * Composite icons showing "User via [Medium]" patterns for social engineering
 * attack vectors. Uses CompositeIcon component to layer base icon with badge.
 */

import type { ReactNode } from 'react';

import { CompositeIcon } from '@/sections/attacks/attackPaths/components/icons/CompositeIcon';
import type {
  IconDefinition,
  IconRenderer,
  IconRenderProps,
} from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';
import { iconRenderers } from '@/sections/attacks/attackPaths/components/icons/registry/icon-renderers-map';

// ============================================================================
// Social Engineering Icon Definitions
// ============================================================================

/**
 * Map of social engineering icon IDs to their composite icon configurations.
 */
const socialIcons = new Map<
  string,
  { baseIcon: string; badgeIcon: string; label: string }
>([
  // Standard mediums (lucide icons - now registered)
  [
    'UserEmail',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'lucide:Mail',
      label: 'User (Email)',
    },
  ],
  [
    'UserSMS',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'lucide:MessageSquare',
      label: 'User (SMS)',
    },
  ],
  [
    'UserPhone',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'lucide:Phone',
      label: 'User (Phone)',
    },
  ],
  [
    'User2FA',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'lucide:Shield',
      label: 'User (2FA)',
    },
  ],

  // Brand platforms (using local SVG files from /public/icons/brands/)
  [
    'UserSlack',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'brand:slack',
      label: 'User (Slack)',
    },
  ],
  [
    'UserLinkedIn',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'brand:linkedin',
      label: 'User (LinkedIn)',
    },
  ],
  [
    'UserFacebook',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'brand:facebook',
      label: 'User (Facebook)',
    },
  ],
  [
    'UserTwitter',
    {
      baseIcon: 'lucide:User',
      badgeIcon: 'brand:x',
      label: 'User (Twitter/X)',
    },
  ],
]);

// ============================================================================
// Social Renderer Implementation
// ============================================================================

const socialRenderer: IconRenderer = {
  render: (name: string, { size, className }: IconRenderProps): ReactNode => {
    const config = socialIcons.get(name);
    if (!config) return null;

    return (
      <CompositeIcon
        baseIcon={config.baseIcon}
        badgeIcon={config.badgeIcon}
        baseSize={size}
        badgeSize={Math.floor((size || 24) * 0.4)} // Badge is 40% of base size
        className={className}
      />
    );
  },

  validate: (name: string): boolean => socialIcons.has(name),

  getIcon: (name: string) => {
    const config = socialIcons.get(name);
    if (!config) return null;

    // Return a wrapper component that accepts size and className
    return function WrappedSocialIcon({
      size = 24,
      className,
    }: {
      size?: number;
      className?: string;
    }) {
      return (
        <CompositeIcon
          baseIcon={config.baseIcon}
          badgeIcon={config.badgeIcon}
          baseSize={size}
          badgeSize={Math.floor(size * 0.4)} // Badge is 40% of base size
          className={className}
        />
      );
    };
  },

  getAll: (): IconDefinition[] =>
    Array.from(socialIcons.entries()).map(([id, config]) => ({
      id: `social:${id}`,
      component: function SocialIconComponent({
        className,
      }: {
        className?: string;
      }) {
        return (
          <CompositeIcon
            baseIcon={config.baseIcon}
            badgeIcon={config.badgeIcon}
            baseSize={24}
            badgeSize={10} // 40% of 24px base
            className={className}
          />
        );
      },
      label: config.label,
      category: 'social',
    })),
};

// ============================================================================
// Register Renderer
// ============================================================================

iconRenderers.set('social', socialRenderer);

export { socialRenderer };
